import { Sparkles, Home, MapPin, Shield, TrendingUp } from "lucide-react";

const STEP_CARDS = [
  {
    icon: Sparkles, title: "Maximise your income",
    desc: "Our flexible lettings strategy keeps occupancy high while chasing the best nightly rate possible.",
  },
  {
    icon: Home, title: "Condition matters",
    desc: "Well-presented properties earn noticeably more. We'll advise on the small touches that lift your nightly rate.",
  },
  {
    icon: MapPin, title: "Data-driven pricing",
    desc: "Hourly dynamic pricing powered by real-time market data, tuned to your postcode and season.",
  },
  {
    icon: Shield, title: "Fully managed",
    desc: "Cleaning, linens, guest comms and maintenance, all handled by your local team, end to end.",
  },
  {
    icon: TrendingUp, title: "Your earning potential",
    desc: "Share your details and we'll send a personalised income estimate within 24 hours. Free, no obligation..",
  },
];

const widgetBox = { background: "#F4F1E7", border: "1px solid #E8E4D5", borderRadius: 10, padding: "14px 16px", marginTop: 16 };
const wLabel = { fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "#5C7268" };

/* Step 0 — vertical revenue bars */
function BarsGraphic() {
  const bars = [3, 5, 4, 6, 5, 7, 6]; const active = 5;
  return (
    <div style={widgetBox}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <span style={wLabel}>Avg nightly rate</span>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#CD533B", fontWeight: 600 }}>↑ 8%</span>
      </div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 800, color: "#112215", margin: "0 0 10px", letterSpacing: "-0.02em" }}>£165</p>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 5, height: 36 }}>
        {bars.map((h, i) => <div key={i} style={{ width: 7, height: `${(h / 7) * 100}%`, borderRadius: 3, background: i === active ? "#112215" : "#E8E4D5" }} />)}
      </div>
    </div>
  );
}

/* Step 1 — condition scale */
function ConditionGraphic() {
  const levels = ["Basic", "Average", "Good", "Luxury"]; const active = 2;
  return (
    <div style={widgetBox}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <span style={wLabel}>Condition uplift</span>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#CD533B", fontWeight: 600 }}>↑ vs basic</span>
      </div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 800, color: "#112215", margin: "0 0 12px", letterSpacing: "-0.02em" }}>+22%</p>
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
        {levels.map((_, i) => <div key={i} style={{ flex: 1, height: 6, borderRadius: 3, background: i <= active ? "#112215" : "#E8E4D5" }} />)}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        {levels.map((l, i) => <span key={l} style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: i === active ? 700 : 400, color: i === active ? "#112215" : "#8A9B92" }}>{l}</span>)}
      </div>
    </div>
  );
}

/* Step 2 — price sparkline (SVG area) */
function SparklineGraphic() {
  const pts = [40, 52, 46, 60, 55, 70, 64];
  const W = 100, H = 40, pad = 4;
  const max = Math.max(...pts), min = Math.min(...pts);
  const xy = pts.map((v, i) => [(i / (pts.length - 1)) * (W - 2 * pad) + pad, H - pad - ((v - min) / (max - min)) * (H - 2 * pad)]);
  const line = xy.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
  const area = `${line} L${W - pad},${H - pad} L${pad},${H - pad} Z`;
  return (
    <div style={widgetBox}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
        <span style={wLabel}>Tonight's rate</span>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#CD533B", fontWeight: 600 }}>↑ 12%</span>
      </div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 26, fontWeight: 800, color: "#112215", margin: "0 0 8px", letterSpacing: "-0.02em" }}>£189</p>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 40, display: "block" }}>
        <path d={area} fill="#112215" opacity="0.1" />
        <path d={line} fill="none" stroke="#112215" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={xy[xy.length - 1][0]} cy={xy[xy.length - 1][1]} r="2.5" fill="#112215" />
      </svg>
    </div>
  );
}

/* Step 3 — task checklist */
function ChecklistGraphic() {
  const tasks = [
    { label: "Linen refresh", active: true },
    { label: "Deep clean", active: false },
    { label: "Inspection", active: false },
  ];
  return (
    <div style={widgetBox}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <span style={wLabel}>Next clean</span>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#112215" }}>Today 11am</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {tasks.map(t => (
          <div key={t.label} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.active ? "#112215" : "#E8E4D5", flexShrink: 0 }} />
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: t.active ? "#112215" : "#8A9B92", fontWeight: t.active ? 600 : 400 }}>{t.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Step 4 — occupancy gauge (SVG donut) */
function GaugeGraphic() {
  const pct = 84, R = 26, C = 2 * Math.PI * R;
  return (
    <div style={widgetBox}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
        <span style={wLabel}>Est. monthly</span>
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: "#CD533B", fontWeight: 600 }}>↑ 15%</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <svg viewBox="0 0 64 64" style={{ width: 64, height: 64, flexShrink: 0 }}>
          <circle cx="32" cy="32" r={R} fill="none" stroke="#E8E4D5" strokeWidth="6" />
          <circle cx="32" cy="32" r={R} fill="none" stroke="#112215" strokeWidth="6" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C - (pct / 100) * C} transform="rotate(-90 32 32)" />
          <text x="32" y="36" textAnchor="middle" fontFamily="Inter, sans-serif" fontSize="13" fontWeight="700" fill="#112215">{pct}%</text>
        </svg>
        <div>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 800, color: "#112215", margin: 0, letterSpacing: "-0.02em" }}>£3,240</p>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: "#5C7268", margin: "2px 0 0" }}>avg occupancy</p>
        </div>
      </div>
    </div>
  );
}

const GRAPHICS = [BarsGraphic, ConditionGraphic, SparklineGraphic, ChecklistGraphic, GaugeGraphic];

export default function EnrichmentInfoPanel({ step = 0 }) {
  const card = STEP_CARDS[step] || STEP_CARDS[0];
  const Graphic = GRAPHICS[step] || GRAPHICS[0];
  const Icon = card.icon;
  return (
    <div className="enrichment-info-side" style={{ background: "#EDE9DC", padding: "36px 30px", display: "flex", flexDirection: "column", justifyContent: "center", borderLeft: "1px solid #E8E4D5" }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "24px 22px", border: "1px solid #E8E4D5", boxShadow: "0 6px 20px rgba(0,0,0,0.05)" }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "#EDE9DC", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
          <Icon size={20} color="#112215" />
        </div>
        <h4 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 17, color: "#112215", margin: "0 0 8px", letterSpacing: "-0.01em" }}>{card.title}</h4>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: "#5C7268", lineHeight: 1.55, margin: 0 }}>{card.desc}</p>
        <Graphic />
      </div>
    </div>
  );
}